import React from "react";
import { Grid, Row, Col, Divider, Text, FlexboxGrid } from "rsuite";
import { useSelector } from "react-redux";
import {
  selectConfigExpressTaxGrossReceiptsLabel,
  selectConfigExpressTaxGrossReceiptsDescription,
  selectConfigExpressTaxDeductionsLabel,
  selectConfigExpressTaxDeductionsDescription,
  selectConfigExpressTaxTaxableReceiptsLabel,
  selectConfigExpressTaxHasCollectionAgreement,
  selectConfigExpressTaxConfig,
  deriveReportRevenuePlatformsThatRemitLabel,
  deriveReportRevenuePlatformsThatDoNotRemitLabel,
  selectConfigExpressTaxEnableDeductions
} from "../../../store/slices/config/configSelectors";
import { useMemo } from "react";
import { convertNumberToCurrency } from "../../../common/utils";
import isNil from 'lodash/isNil';
import ProviderValuesRow from "./ProviderValuesRow";

const calculateTotalTaxDueForProvider = (taxConfig, provider) => {
  return taxConfig.reduce((total, item) => {
    const providerTaxType = provider.taxTypes[item.key];

    if (providerTaxType) {
      total += item.breakdown.reduce((sum, breakdown) => {
        return sum + (parseFloat(providerTaxType[breakdown.key]) || 0);
      }, 0);
    }

    return total;
  }, 0);
}

const DividerRow = () => (
  <Row>
    <Col xs={24}>
      <Divider></Divider>
    </Col>
  </Row>
)

const TaxPayableBreakdownTable = ({ license, periodLabel, processingFee = null, totalPayment = null, offsetRightStyle = { marginRight: '19px' } }) => {
  const selectGrossReceiptsLabel = useSelector(selectConfigExpressTaxGrossReceiptsLabel);
  const selectGrossReceiptsDescription = useSelector(selectConfigExpressTaxGrossReceiptsDescription);
  const selectDeductionsLabel = useSelector(selectConfigExpressTaxDeductionsLabel);
  const selectDeductionsDescription = useSelector(selectConfigExpressTaxDeductionsDescription);
  const selectTaxableReceiptsLabel = useSelector(selectConfigExpressTaxTaxableReceiptsLabel);
  const selectHasCollectionAgreement = useSelector(selectConfigExpressTaxHasCollectionAgreement);
  const selectTaxConfig = useSelector(selectConfigExpressTaxConfig);
  const platformsThatRemitLabel = useSelector(deriveReportRevenuePlatformsThatRemitLabel);
  const platformsThatDontRemitLabel = useSelector(deriveReportRevenuePlatformsThatDoNotRemitLabel);
  const showDeductions = useSelector(selectConfigExpressTaxEnableDeductions);

  const sortOtherLast = (provider) => {
    return provider.key === "other" ? 1 : 0; // "other" comes last
  };

  // Sort so that that platforms that do not remit tot ("other") provider appears last
  const sortedLicenses = useMemo(() => {
    return [...license].sort((a, b) => sortOtherLast(a) - sortOtherLast(b));
  }, [license])


  const xsRowValueWidth = useMemo(() => {
    return selectHasCollectionAgreement ? 6 : 12;
  }, [selectHasCollectionAgreement]);

  const mdRowValueWidth = useMemo(() => {
    return selectHasCollectionAgreement ? 4 : 8
  }, [selectHasCollectionAgreement]);

  return (
    <Grid fluid>
      <FlexboxGrid as={Row} className="mb-12" align="bottom" style={offsetRightStyle}>
        <FlexboxGrid.Item as={Col} xs={12} md={16}></FlexboxGrid.Item>
        {
          selectHasCollectionAgreement && (
            <FlexboxGrid.Item as={Col} xs={xsRowValueWidth} md={mdRowValueWidth}>
              <Text className="text-small" align="right">{platformsThatRemitLabel}</Text>
            </FlexboxGrid.Item>
          )
        }
        <FlexboxGrid.Item as={Col} xs={xsRowValueWidth} md={mdRowValueWidth}>
          <Text className="text-small" align="right">{platformsThatDontRemitLabel}</Text>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <Row>
        <div role="cell">
          <ProviderValuesRow
            label={selectGrossReceiptsLabel}
            infoText={selectGrossReceiptsDescription}
            sortedLicenses={sortedLicenses}
            keyPrefix="sales"
            valueGetter={(provider) => provider.sales}
            xsRowValueWidth={xsRowValueWidth}
            mdRowValueWidth={mdRowValueWidth}
            rowClass="mb-12"
          />
          {
            showDeductions && (
              <ProviderValuesRow
                label={selectDeductionsLabel}
                infoText={selectDeductionsDescription}
                sortedLicenses={sortedLicenses}
                keyPrefix="deductions"
                valueGetter={(provider) => provider.deductions}
                xsRowValueWidth={xsRowValueWidth}
                mdRowValueWidth={mdRowValueWidth}
                rowClass="mb-12"
              />
            )
          }

          <div role="grid">
            <DividerRow />
          </div>
          <ProviderValuesRow
            label={selectTaxableReceiptsLabel}
            sortedLicenses={sortedLicenses}
            keyPrefix="taxable_receipts"
            valueGetter={(provider) => provider.taxableReceipts}
            xsRowValueWidth={xsRowValueWidth}
            mdRowValueWidth={mdRowValueWidth}
            bold={true}
            rowClass="mb-12"
          />
        </div>
      </Row>

      {
        selectTaxConfig.map(
          (tax, index) => {
             // only include tax type if it is in the config and there is a matching item in the data
            const dataExistsForConfigKey = sortedLicenses.some(provider => provider.taxTypes?.[tax.key]);
            if (dataExistsForConfigKey) {
              return (
                <Row key={`tax_item_${index}`} style={{ padding: '7px 0px 7px 15px', backgroundColor: index % 2 === 0 ? '#f1f1f1' : '#ffffff' }}>
                  <Col xs={24}>
                    <>
                      <ProviderValuesRow
                        label={tax.label}
                        infoText={tax.infoText}
                        sortedLicenses={sortedLicenses}
                        keyPrefix="tax_item_provider_value_"
                        valueGetter={(provider) => provider.taxTypes[tax.key]?.total}
                        rowClass="mb-4"
                        xsRowValueWidth={xsRowValueWidth}
                        mdRowValueWidth={mdRowValueWidth}
                      />
                      {
                        tax.breakdown.map(
                          (breakdown, index) => (
                            <ProviderValuesRow
                              key={`tax-breakdown-${index}`}
                              label={breakdown.label}
                              infoText={breakdown.infoText}
                              sortedLicenses={sortedLicenses}
                              keyPrefix="tax_item_breakdown_"
                              valueGetter={(provider) => provider.taxTypes?.[tax.key]?.[breakdown.key]}
                              labelClass="pl-16"
                              rowClass="text-small"
                              xsRowValueWidth={xsRowValueWidth}
                              mdRowValueWidth={mdRowValueWidth}
                            />
                          )
                        )
                      }
                    </>
                  </Col>
                </Row>
              )
            }
          }
        )
      }
      <DividerRow />
      <Row style={offsetRightStyle}>
        <Col xs={12} md={16}>
          <Text className={`text-bold`}>Total Payment Due {periodLabel}</Text>
        </Col>
        {
          sortedLicenses.map(
            (provider, index) => (
              <Col key={`total_payment_due_provider_value_${index}`} xs={xsRowValueWidth} md={mdRowValueWidth}>
                <Text align="right">{convertNumberToCurrency(calculateTotalTaxDueForProvider(selectTaxConfig, provider))}</Text>
              </Col>
            )
          )
        }
      </Row>

      {
        !isNil(processingFee) && !isNil(totalPayment) && (
          <>
            <DividerRow />
            <Row style={offsetRightStyle}>
              <Col xs={12} md={16}>
                <Text>Processing Fee</Text>
              </Col>
              <Col xs={12} md={8}>
                <Text align="right">{convertNumberToCurrency(processingFee)}</Text>
              </Col>
            </Row>

            <Row className="mt-8" style={offsetRightStyle}>
              <Col xs={12} md={16}>
                <Text className={`text-bold`}>Total Paid</Text>
              </Col>
              <Col xs={12} md={8}>
                <Text className={`text-bold`} align="right">{convertNumberToCurrency(totalPayment)}</Text>
              </Col>
            </Row>
          </>
        )
      }
    </Grid>
  );
};

export default TaxPayableBreakdownTable;
